import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { ReviewResult } from "@redotech/redo-model/agents/review-result";
import { Return } from "@redotech/redo-model/return";
import {
  RedoBadge,
  RedoBadgeProps,
} from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import StarsIcon from "@redotech/redo-web/arbiter-icon/stars-01-gradient.svg";
import ThumbsDownIcon from "@redotech/redo-web/arbiter-icon/thumbs-down.svg";
import ThumbsUpIcon from "@redotech/redo-web/arbiter-icon/thumbs-up.svg";
import { Card } from "@redotech/redo-web/card";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { assertNever } from "@redotech/util/type";
import { memo, useState } from "react";

export const AiReview = memo(function AiReview({
  return_,
}: {
  return_: Return;
}) {
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);

  const handleFeedback = useHandler(async (isPositive: boolean) => {
    try {
      if (!review.spanId) {
        return;
      }
      await rpcClient.provideAiFeedback({
        spanId: review.spanId,
        projectName: "return-reviews",
        scores: { userScore: isPositive ? 1 : 0 },
      });
      setFeedbackSubmitted(true);
    } catch (error) {
      console.error("Failed to submit feedback:", error);
    }
  });

  if (!return_.agentReview) return null;
  const review = return_.agentReview;

  return (
    <Card
      customBackgroundColor="var(--redo-colors-background-bg-tertiary)"
      gap="md"
      p="xl"
      radius="md"
      title={
        <Flex align="center" dir="row">
          <StarsIcon height={24} />
          <Text fontSize="sm" fontWeight="semibold">
            AI Review
          </Text>
          <RedoBadge {...getRiskBadgeProps(review.risk)} />
        </Flex>
      }
    >
      <Text fontSize="sm">{review.explanation}</Text>

      <Flex align="center" dir="row" gap="md" justify="flex-end" mt="md">
        {review.spanId ? (
          feedbackSubmitted ? (
            <Text color="text.secondary" fontSize="xs">
              Thank you for your feedback!
            </Text>
          ) : (
            <>
              <Text color="text.secondary" fontSize="xs">
                Was this review helpful?
              </Text>
              <RedoButton
                aria-label="Thumbs up"
                IconLeading={() => <ThumbsUpIcon height={16} />}
                onClick={() => handleFeedback(true)}
                size="xs"
              />
              <RedoButton
                aria-label="Thumbs down"
                IconLeading={() => <ThumbsDownIcon height={16} />}
                onClick={() => handleFeedback(false)}
                size="xs"
              />
            </>
          )
        ) : null}
      </Flex>
    </Card>
  );
});

function getRiskBadgeProps(risk: ReviewResult["risk"]): RedoBadgeProps {
  switch (risk) {
    case "low":
      return { color: "success", text: "Low risk" };
    case "medium":
      return { color: "warning", text: "Medium risk" };
    case "high":
      return { color: "error", text: "High risk" };
    default:
      assertNever(risk);
  }
}
